import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { ErrorCode, PortalApiError, StartedCheckout, FirstPaymentType, ConsumerFeeItem } from '@afterpaytouch/portal-api'
import {
  closeCheckout,
  crossRegionUserSignIn,
  getCheckout,
  updateCheckout,
  addCashCustomerDetails,
  identityVerify,
  setupPurchase,
  startCheckout,
  confirmCheckout,
  updatePreferredPayday,
} from '../'
import { Checkout, CashAppObject } from '@afterpaytouch/portal-api/types'
import {
  CheckoutError,
  checkoutErrors,
  CheckoutStatus,
  ConfirmedCheckout,
  EnrichedBankAccountDetails,
  EnrichedCardDetails,
  InitialData,
  lastPaymentDate,
  nonTerminalErrors,
  NotificationStatus,
  otbWhitelist,
  StartedAmountData,
  TerminalErrorCode,
  errorsEligibleForHardIdVerification,
  OAuthAuthorizationFailedCallbackUrlArgs,
  OAuthAuthorizationSuccessCallbackUrlArgs,
  CheckoutPreferredCardsAndBankAccounts,
  AllowedQuery,
  SessionIdState,
  SessionIdVisibility,
  IntegrationContext,
  DataVault,
  EnrichedItem,
  AU_REG_ERROR_CODES,
} from '../../model'
import { enrichBankAccountByPaymentType, enrichBankAccountOverdue, PreferredBankAccount, selectPreferredBankAccount } from '../../utils/bank-account'
import { enrichCard, PreferredCard, selectPreferredCard, filterCardsForPreferredForOverduePayments } from '../../utils/credit-card'
import { camelCase } from '@afterpay/utils/string'
import { PURGE } from 'redux-persist'
import { retrieveCards, updatePaymentOption } from './actions'
import { PaymentType, CashAppPayDetail, Money } from '@afterpay/types'
import { isNullOrUndefined } from '@afterpay/utils'
import { enrichPaymentSchedules } from '../../utils/payment-schedule'
import { getEnrichedCardsAndBankAccounts } from '../../utils/card-and-bank'
import { getFeatureFromPaymentType, isAfterpayFinanceFeeEligible } from '../../utils/payment-type'
import { buildDataVault, mergeOffersIntoItemsVault } from './helper'

export interface CheckoutState {
  initial: InitialData
  status: CheckoutStatus
  checkout?: Checkout
  started?: StartedCheckout
  initialStartedAmount?: StartedAmountData
  confirmed?: ConfirmedCheckout
  error?: CheckoutError
  firstPaymentType: FirstPaymentType
  notificationStatus: NotificationStatus
  cashAppPays: CashAppPayDetail[]
  cards: EnrichedCardDetails[]
  bankAccounts: EnrichedBankAccountDetails[]
  preferredCard?: PreferredCard
  preferredBankAccount?: PreferredBankAccount
  referrer?: string | null
  doItemsReconcile?: boolean
  cashTooltipShown?: boolean
  creditCheckAgree: boolean
  cashAppObjectUnderTest?: CashAppObject
  allowedQueryParam: AllowedQuery
  isEnabledAutoPay: boolean
  termsDisclosureAgree?: boolean
  loggedInLoadingMessageKey?: string | null
  consumerFees?: ConsumerFeeItem[]
  sessionId: SessionIdState
  confirmTermsViewed: string | null
  integrationContext?: IntegrationContext
  items: DataVault<EnrichedItem>
}

export const checkoutInitialState: CheckoutState = {
  status: CheckoutStatus.Initial,
  initial: {
    token: '',
    shippingOptionRequired: true,
    isIframe: false,
    isWindowed: false,
    entryPoint: null,
    pickup: false,
    buyNow: false,
    zoid: '',
    relativeCallbackUrl: '',
    // @ts-expect-error: OPERATION BLEED STOPPER
    clientId: null,
    responseType: null,
    // @ts-expect-error: OPERATION BLEED STOPPER
    redirectUri: null,
    // @ts-expect-error: OPERATION BLEED STOPPER
    scope: null,
    // @ts-expect-error: OPERATION BLEED STOPPER
    state: null,
    checkoutButtonLabel: '',
    // @ts-expect-error: OPERATION BLEED STOPPER
    visitorId: null,
    // @ts-expect-error: OPERATION BLEED STOPPER
    addressMode: null,
    isAfterpayInAppSession: false,
  },
  firstPaymentType: FirstPaymentType.Unknown,
  notificationStatus: NotificationStatus.Unread,
  cards: [],
  bankAccounts: [],
  cashAppPays: [],
  doItemsReconcile: true,
  creditCheckAgree: false,
  allowedQueryParam: {
    hideHeader: false,
    // TODO improve the skipPrefill by using this state CHECKOUT-3939
    skipPrefill: false,
  },
  isEnabledAutoPay: true,
  loggedInLoadingMessageKey: null,
  sessionId: {
    visibility: SessionIdVisibility.HIDDEN,
  },
  items: {
    ids: [],
    data: {},
  },
}

const getFirstPaymentType = (paymentUpFront: boolean, downPaymentType: FirstPaymentType): FirstPaymentType => {
  if (paymentUpFront) {
    return downPaymentType !== '' ? downPaymentType : FirstPaymentType.PaymentUpfront
  } else {
    return FirstPaymentType.NoPaymentUpfront
  }
}

const isPufOrNpuf = (amountDueToday: Money): FirstPaymentType.PaymentUpfront | FirstPaymentType.NoPaymentUpfront => {
  // if amountDueToday is zero, then we know that isFirstPaymentUpFront or paymentUpFront is false
  // https://github.com/AfterpayTouch/pay-later/blob/8b57f7c5500789a50677a7d4820c7e41ac0ab83b/applications/portal-api/src/main/java/com/touch/paylater/portalapi/infrastructure/checkout/CheckoutOperator.java#L1503

  // we can't differentiate b/w LPUF, VPUF and FPUF from amountDueToday
  if (amountDueToday.amount === '0.00') {
    return FirstPaymentType.NoPaymentUpfront
  }

  return FirstPaymentType.PaymentUpfront
}

const addToRelativeCallbackUrlQueryArguments = (key: string, value: string, queryArgs: string): string => {
  if (key.length > 0 && value.length > 0) {
    return `${queryArgs}&${key}=${value}`
  }
  return queryArgs
}

const checkoutSlice = createSlice({
  name: 'checkout',
  initialState: checkoutInitialState,
  reducers: {
    setAllowedQueryParam(state: CheckoutState, action: PayloadAction<AllowedQuery>) {
      state.allowedQueryParam = action.payload
    },
    setInitialData(state: CheckoutState, action: PayloadAction<InitialData>) {
      state.initial = action.payload
      state.error = undefined
    },
    setReferrer(state: CheckoutState, action: PayloadAction<string | null>) {
      state.referrer = action.payload
    },
    setNotificationStatus(state: CheckoutState, action: PayloadAction<NotificationStatus>) {
      state.notificationStatus = action.payload
    },
    setCheckoutError(state: CheckoutState, action: PayloadAction<CheckoutError>) {
      state.status = CheckoutStatus.Error
      state.error = action.payload
    },
    resetCheckoutError(state: CheckoutState) {
      state.error = undefined
    },
    setEFAFinished(state: CheckoutState) {
      if (typeof state.error !== 'undefined') {
        const extra = state.error
        state.error.extra = {
          isEFAFinished: true,
          ...extra,
        }
      }
    },
    setSessionIdVisibility(state: CheckoutState, action: PayloadAction<SessionIdVisibility>) {
      state.sessionId.visibility = action.payload
    },
    resetSessionId(state: CheckoutState) {
      state.sessionId = checkoutInitialState.sessionId
    },
    resetPreferredPaymentMethodErrors(state: CheckoutState) {
      /*
         On the Summary page an error is displayed, in some form, when any of the below values are defined.
         When a new payment method is added the below values are no longer relevant, as the process of adding
         a new payment method ensures that it is valid. As a result we want to remove those errors from the
         Summary page from being displayed.
         TODO: Consider moving the logic that sets the below values to the backend.
       */
      if (state.preferredCard !== undefined) {
        state.preferredCard.card = null
        state.preferredCard.reason = null
        // @ts-expect-error: OPERATION BLEED STOPPER
        state.preferredBankAccount.reason = null
        // @ts-expect-error: OPERATION BLEED STOPPER
        state.preferredBankAccount.account = null
      }
    },
    resetToInitialCheckoutAmount(state: CheckoutState) {
      // @ts-expect-error: OPERATION BLEED STOPPER
      const { paymentUpFront, downPaymentType } = state.initialStartedAmount
      // @ts-expect-error: OPERATION BLEED STOPPER
      state.started = {
        ...state.started,
        ...state.initialStartedAmount,
      }
      state.firstPaymentType = getFirstPaymentType(paymentUpFront, downPaymentType)
    },
    addOAuthAuthorizationCodeIntoCallbackUrlQueryArgs(state: CheckoutState, action: PayloadAction<OAuthAuthorizationSuccessCallbackUrlArgs>) {
      const { code, state: oAuthState } = action.payload
      // @ts-expect-error: OPERATION BLEED STOPPER
      state.confirmed.callbackUrlQueryArgs = addToRelativeCallbackUrlQueryArguments('code', code, state.confirmed.callbackUrlQueryArgs)
      // @ts-expect-error: OPERATION BLEED STOPPER
      state.confirmed.callbackUrlQueryArgs = addToRelativeCallbackUrlQueryArguments('state', oAuthState ?? '', state.confirmed.callbackUrlQueryArgs)
    },
    addOAuthAuthorizationErrorIntoCallbackUrlQueryArgs(state: CheckoutState, action: PayloadAction<OAuthAuthorizationFailedCallbackUrlArgs>) {
      const { error, state: oAuthState } = action.payload
      // @ts-expect-error: OPERATION BLEED STOPPER
      state.confirmed.callbackUrlQueryArgs = addToRelativeCallbackUrlQueryArguments('error', error, state.confirmed.callbackUrlQueryArgs)
      // @ts-expect-error: OPERATION BLEED STOPPER
      state.confirmed.callbackUrlQueryArgs = addToRelativeCallbackUrlQueryArguments('state', oAuthState ?? '', state.confirmed.callbackUrlQueryArgs)
    },
    setCheckoutStatusToBeLoaded(state: CheckoutState) {
      state.status = CheckoutStatus.Loaded
    },
    setCheckoutStatus(state: CheckoutState, action: PayloadAction<CheckoutStatus>) {
      state.status = action.payload
    },
    setDoItemsReconcile(state: CheckoutState, action: PayloadAction<boolean>) {
      state.doItemsReconcile = action.payload ?? true
    },
    setCardsAndBankAccounts(state: CheckoutState, action: PayloadAction<CheckoutPreferredCardsAndBankAccounts>) {
      const { enabledCardTypes, savedBankAccounts, savedCards } = action.payload
      const preferredCard = filterCardsForPreferredForOverduePayments(savedCards)
      const preferredBankAccount = null
      const enrich = enrichCard({ enabledCardTypes })
      const ePreferredCard = preferredCard === null ? null : enrich(preferredCard)
      state.cards = savedCards?.map(enrich) ?? []
      state.preferredCard = selectPreferredCard(ePreferredCard, state.cards)
      state.bankAccounts = savedBankAccounts?.map((account) => enrichBankAccountOverdue(account)) ?? []
      const ePreferredBankAccount = preferredBankAccount === null ? null : enrichBankAccountOverdue(preferredBankAccount)
      state.preferredBankAccount = selectPreferredBankAccount(ePreferredBankAccount, state.bankAccounts)
    },
    setCardForOverdueACH(state: CheckoutState, action: PayloadAction<PreferredCard>) {
      state.preferredCard = action.payload
    },
    setCashAppPay(state: CheckoutState, action: PayloadAction<CashAppPayDetail[] | undefined>) {
      if (isNullOrUndefined(action.payload)) {
        state.cashAppPays = []
        return
      }
      state.cashAppPays = action.payload
    },
    setCardsForExpiryLimit(state: CheckoutState, action: PayloadAction<number>) {
      if (isNullOrUndefined(state.started)) {
        state.cards = []
        state.preferredCard = selectPreferredCard(null, state.cards)
        return
      }
      const cardExpiryLimitInMonths = action.payload
      const enrich = enrichCard({
        enabledCardTypes: state.started.enabledCardTypes,
        lastPaymentDate: lastPaymentDate(state.started.paymentSchedules),
        cardExpiryLimitInMonths,
        paymentType: PaymentType.PCL,
      })
      const ePreferredCard = state.started?.preferredCard === null ? null : enrich(state.started?.preferredCard)
      state.cards = state.started.savedCards?.map(enrich) ?? []
      state.preferredCard = selectPreferredCard(ePreferredCard, state.cards)
    },
    resetPreferredCardReason(state: CheckoutState) {
      // @ts-expect-error: OPERATION BLEED STOPPER
      state.preferredCard = { ...state.preferredCard, reason: null }
    },
    setCashTooltipShown(state: CheckoutState, action: PayloadAction<boolean>) {
      state.cashTooltipShown = action.payload
    },
    setCreditCheckAgree(state: CheckoutState, action: PayloadAction<boolean>) {
      state.creditCheckAgree = action.payload
    },
    setTermsDisclosureAgree(state: CheckoutState, action: PayloadAction<boolean | undefined>) {
      state.termsDisclosureAgree = action.payload
    },
    setPreferredCard(state: CheckoutState, action: PayloadAction<PreferredCard>) {
      state.preferredCard = action.payload
    },
    setSavedCards(state: CheckoutState, action: PayloadAction<EnrichedCardDetails[]>) {
      state.cards = action.payload
    },
    setEnabledPBIAutoPay(state: CheckoutState, action: PayloadAction<boolean>) {
      state.isEnabledAutoPay = action.payload
    },
    setLoggedInLoadingMessageKey(state: CheckoutState, action: PayloadAction<string | null>) {
      state.loggedInLoadingMessageKey = action.payload
    },
    setConfirmTermsViewed(state: CheckoutState, action: PayloadAction<string | null>) {
      state.confirmTermsViewed = action.payload
    },
    setIntegrationContext(state: CheckoutState, action: PayloadAction<IntegrationContext>) {
      state.integrationContext = action.payload
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(setupPurchase.rejected, (state) => {
        state.status = CheckoutStatus.SetupPaymentMethodError
      })
      .addCase(updateCheckout.pending, (state) => {
        state.error = undefined
        state.status = CheckoutStatus.Updating
      })
      .addCase(updateCheckout.fulfilled, (state, action) => {
        const { paymentUpFront, downPaymentType } = action.payload

        // @ts-expect-error: OPERATION BLEED STOPPER
        state.started = {
          ...state.started,
          ...action.payload,
        }

        state.firstPaymentType = getFirstPaymentType(paymentUpFront, downPaymentType)
        state.status = CheckoutStatus.Started
      })
      .addCase(updateCheckout.rejected, (state) => {
        // when deferred shipping and update fails we should show the unable to update error
        if (!state.initial.shippingOptionRequired) {
          state.error = {
            error: checkoutErrors[ErrorCode.UnableToUpdateCheckout],
          }
        }
        state.status = CheckoutStatus.UpdateError
      })
      .addCase(getCheckout.fulfilled, (state, action) => {
        // PAYL-6623 Minimum not met error declined page
        // Please note that this statusCode is NOT a http status code. It's just a custom status code that
        // the backend has included in the body of the response.
        const belowMinimumOrderThreshold = action.payload.statusCode === 402
        state.status = belowMinimumOrderThreshold ? CheckoutStatus.Error : CheckoutStatus.Loaded
        state.checkout = action.payload

        if (belowMinimumOrderThreshold) {
          state.error = {
            error: checkoutErrors[ErrorCode.MinimumNotMet],
            errorCode: ErrorCode.MinimumNotMet,
            extra: {
              minimumOrderValue: action.payload.minimumOrderValue,
            },
          }
        }

        const items = action.payload.items ?? []
        state.items = buildDataVault(items, {
          childPropertyName: 'childId',
          getParentId: (i) => i.parentId,
          getChildValue: (child) => child.id,
        })
      })
      .addCase(addCashCustomerDetails.fulfilled, (state, action) => {
        state.cashAppObjectUnderTest = action.payload
      })
      .addCase(retrieveCards.fulfilled, (state, action) => {
        const { savedCards, traceId } = action.payload
        // @ts-expect-error: OPERATION BLEED STOPPER
        const { enabledCardTypes, paymentSchedules } = state.started
        // @ts-expect-error: OPERATION BLEED STOPPER
        state.started.traceId = traceId
        // @ts-expect-error: OPERATION BLEED STOPPER
        state.started.savedCards = savedCards
        const enrich = enrichCard({ enabledCardTypes, lastPaymentDate: lastPaymentDate(paymentSchedules) })
        state.cards = savedCards?.map(enrich) ?? []
      })
      .addCase(crossRegionUserSignIn.rejected, (state, action) => {
        state.status = CheckoutStatus.Error
        state.error = { error: TerminalErrorCode.Default }
        if (action.meta.rejectedWithValue) {
          // @ts-expect-error: OPERATION BLEED STOPPER
          const { errorCode } = action.payload
          state.error = {
            error: checkoutErrors[errorCode] ?? errorCode,
            errorCode,
          }
        }
      })
      .addCase(closeCheckout, (state) => {
        state.status = CheckoutStatus.Closed
        state.cards = []
        state.preferredCard = undefined
        state.preferredBankAccount = undefined
        state.firstPaymentType = FirstPaymentType.Unknown
        state.notificationStatus = NotificationStatus.Unread
      })
      .addCase(updatePaymentOption.fulfilled, (state, action) => {
        const {
          paymentSchedules,
          paymentOption,
          savedCards,
          preferredCard,
          savedBankAccounts,
          preferredBankAccount,
          promotion,
          traceId,
          purchasePaymentAgreementDocumentId,
          maximumOrderValue,
          paymentRoutingKey,
        } = action.payload

        if (typeof state.started !== 'undefined') {
          state.started = {
            ...state.started,
            paymentSchedules,
            savedCards,
            promotion: promotion ?? null,
            traceId,
            amountDueToday: paymentOption.amountDueToday,
          }
        }

        // Caveat here is that we only know if it's NPUF or not
        // To differentiate FPUF/VPUF/LPUF, updatePaymentOption API call needs to return
        // downPaymentType + paymentUpFront
        state.firstPaymentType = isPufOrNpuf(paymentOption.amountDueToday)

        if (typeof purchasePaymentAgreementDocumentId !== 'undefined') {
          // @ts-expect-error: OPERATION BLEED STOPPER
          state.started.purchasePaymentAgreementDocumentId = purchasePaymentAgreementDocumentId
        }
        if (typeof maximumOrderValue !== 'undefined') {
          // @ts-expect-error: OPERATION BLEED STOPPER
          state.checkout.maximumOrderValue = maximumOrderValue // for shipping options
        }
        if (typeof paymentRoutingKey !== 'undefined') {
          // @ts-expect-error: OPERATION BLEED STOPPER
          state.checkout.paymentRoutingKey = paymentRoutingKey
        }

        if (paymentOption.paymentType !== PaymentType.PCL) {
          const feature = getFeatureFromPaymentType({ paymentType: PaymentType.PBI, isAFF: isAfterpayFinanceFeeEligible(state.consumerFees) })
          const enrich = enrichCard({
            enabledCardTypes: state.started?.enabledCardTypes ?? [],
            lastPaymentDate: lastPaymentDate(paymentSchedules),
            cardExpiryLimitInMonths: 0,
            paymentType: paymentOption.paymentType,
            feature,
          })
          const ePreferredCard = preferredCard === null ? null : enrich(preferredCard)
          state.cards = savedCards?.map(enrich) ?? []
          state.preferredCard = selectPreferredCard(ePreferredCard, state.cards)
        }
        state.bankAccounts = savedBankAccounts?.map((account) => enrichBankAccountByPaymentType(account, paymentOption.paymentType)) ?? []
        const ePreferredBankAccount = preferredBankAccount === null ? null : enrichBankAccountByPaymentType(preferredBankAccount, paymentOption.paymentType)
        state.preferredBankAccount = selectPreferredBankAccount(ePreferredBankAccount, state.bankAccounts)
      })
      .addCase(PURGE, () => checkoutInitialState)
      .addMatcher(startCheckout.matchPending, (state) => {
        state.status = CheckoutStatus.Starting
        state.error = undefined
      })
      .addMatcher(startCheckout.matchRejected, (state, action) => {
        state.status = CheckoutStatus.Error
        state.error = {
          error: TerminalErrorCode.Default,
        }
        if (action.meta.rejectedWithValue) {
          // @ts-expect-error: OPERATION BLEED STOPPER
          const { errorCode, errorInfo } = action.payload.data
          state.error = {
            error: checkoutErrors[errorCode] ?? errorCode,
            errorCode,
          }
          if (errorCode === ErrorCode.RiskDeclineUnclassified) {
            const rosettaErrorCode = camelCase(errorInfo?.rosettaErrorMessageConf?.errorReasonCode)
            if (rosettaErrorCode !== '') {
              state.error = {
                error: rosettaErrorCode,
                errorCode,
              }
            }
          }

          if (errorCode === ErrorCode.PaywatchCheckFailed) {
            state.error = {
              error: checkoutErrors[errorCode] ?? camelCase(errorCode),
              errorCode,
            }
          }

          if (errorCode === ErrorCode.HardIdCollectionRequired) {
            state.status = CheckoutStatus.IdentityIneligible
            state.error = {
              error: ErrorCode.HardIdCollectionRequired,
              errorCode,
            }
          }

          if (errorsEligibleForHardIdVerification.includes(errorCode)) {
            state.status = CheckoutStatus.IdentityIneligible
            state.error = {
              error: errorCode,
              errorCode: errorInfo,
            }
          }

          if (typeof errorInfo?.max_otb_amount_without_down_payment !== 'undefined' && otbWhitelist.includes(errorCode)) {
            state.error.extra = {
              availableToSpend: errorInfo.max_otb_amount_without_down_payment,
            }
          }
          if (
            errorCode === ErrorCode.MaxActiveOrdersReached &&
            typeof errorInfo?.number_of_orders_to_be_paid !== 'undefined' &&
            typeof errorInfo?.number_of_active_orders !== 'undefined'
          ) {
            state.error = {
              error: TerminalErrorCode.MaxActiveOrdersReachedDetails,
              errorCode: ErrorCode.MaxActiveOrdersReached,
              extra: {
                numberOrdersToPay: errorInfo.number_of_orders_to_be_paid,
                numberActiveOrders: errorInfo.number_of_active_orders,
              },
            }
          }
          if (errorCode === ErrorCode.InvalidAmount && typeof errorInfo?.maximum_order_value !== 'undefined') {
            state.error = {
              error: TerminalErrorCode.InvalidAmount,
              errorCode: ErrorCode.InvalidAmount,
              extra: {
                maximumOrderValue: errorInfo.maximum_order_value,
              },
            }
          }
          if (errorCode === ErrorCode.StateRestrictionsPCL) {
            state.error = {
              error: TerminalErrorCode.StateRestrictionsPCL,
              errorCode: ErrorCode.StateRestrictionsPCL,
            }
            if (typeof errorInfo?.maximum_order_value !== 'undefined') {
              state.error.extra = {
                maximumOrderValue: errorInfo.maximum_order_value,
              }
            } else if (typeof errorInfo?.pay_monthly_only !== 'undefined') {
              state.error.extra = {
                payMonthlyOnly: errorInfo.pay_monthly_only,
              }
            }
          }
          if (errorCode === ErrorCode.IdentityVerificationDeclinedPCL) {
            state.error = {
              error: TerminalErrorCode.IdentityVerificationDeclinedPCL,
              errorCode: ErrorCode.IdentityVerificationDeclinedPCL,
            }
            if (typeof errorInfo?.pay_monthly_only !== 'undefined') {
              state.error.extra = {
                payMonthlyOnly: errorInfo.pay_monthly_only,
              }
            }
          }
          if (errorCode === ErrorCode.ExtendedFraudAlert) {
            state.error = {
              error: TerminalErrorCode.ExtendedFraudAlert,
              errorCode: ErrorCode.ExtendedFraudAlert,
            }
          }
          if (errorCode === ErrorCode.OrderAmountExceedsSpendCap) {
            state.error.extra = {
              availableToSpend: errorInfo.max_otb_amount,
            }
          }

          if (AU_REG_ERROR_CODES.includes(errorCode) && typeof errorInfo?.facility_application_introduction_eligibility !== 'undefined') {
            state.error.extra = {
              ...state.error.extra,
              facilityApplicationIntroductionEligibility: errorInfo.facility_application_introduction_eligibility,
            }
          }
        }
      })
      .addMatcher(startCheckout.matchFulfilled, (state, action) => {
        const {
          consumerAmount,
          consumerFees,
          merchantAmount,
          installmentAmount,
          downPaymentAmount,
          amountDueToday,
          enabledCardTypes,
          savedCards,
          paymentSchedules,
          paymentUpFront,
          downPaymentType,
          preferredCard,
          savedBankAccounts,
          preferredBankAccount,
        } = action.payload

        // At the `/start` endpoint, the default payment type is always PBI.
        const feature = getFeatureFromPaymentType({ paymentType: PaymentType.PBI, isAFF: isAfterpayFinanceFeeEligible(consumerFees) })
        const { enrichedCards, enrichedBankAccounts, enrichedPreferredBankAccount, enrichedPreferredCard } = getEnrichedCardsAndBankAccounts({
          enabledCardTypes,
          paymentSchedules,
          preferredBankAccount,
          preferredCard,
          savedBankAccounts,
          savedCards,
          feature,
        })

        state.status = CheckoutStatus.Started
        state.started = action.payload
        state.cashAppPays = action.payload.cashAppPay ?? []
        state.cards = enrichedCards
        state.preferredCard = enrichedPreferredCard
        state.bankAccounts = enrichedBankAccounts
        state.preferredBankAccount = enrichedPreferredBankAccount
        state.consumerFees = consumerFees

        state.firstPaymentType = getFirstPaymentType(paymentUpFront, downPaymentType)

        if (state.initial.shippingOptionRequired) {
          state.initialStartedAmount = {
            paymentSchedules,
            amountDueToday,
            paymentUpFront,
            consumerAmount,
            merchantAmount,
            installmentAmount,
            downPaymentAmount,
            downPaymentType,
          }
        }

        state.items = mergeOffersIntoItemsVault(state.items, action.payload.offers)
      })
      .addMatcher(confirmCheckout.matchPending, (state) => {
        state.status = CheckoutStatus.Confirming
      })
      .addMatcher(confirmCheckout.matchRejected, (state, action) => {
        state.status = CheckoutStatus.Error
        state.error = { error: TerminalErrorCode.Default }
        if (action.meta.rejectedWithValue) {
          // RTKQ rejected payload is `{ status, data }` where `data` is the Portal API error body.
          // Our `ApiService.getBaseQuery` always wraps axios errors into that shape.
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const portalError: any = (action.payload as any)?.data
          const errorCode = portalError?.errorCode
          const errorInfo = portalError?.errorInfo
          if (typeof errorCode === 'undefined') {
            return
          }
          if (nonTerminalErrors.includes(errorCode)) {
            state.status = CheckoutStatus.Started
          }
          state.error = {
            error: checkoutErrors[errorCode] ?? errorCode,
            errorCode,
          }

          if (typeof errorInfo?.max_otb_amount_without_down_payment !== 'undefined' && otbWhitelist.includes(errorCode)) {
            state.error.extra = {
              availableToSpend: errorInfo.max_otb_amount_without_down_payment,
            }
          }
          if (typeof errorInfo?.maximum_order_value !== 'undefined') {
            state.error.extra = {
              maximumOrderValue: errorInfo.maximum_order_value,
            }
          }
          if (errorCode === ErrorCode.ExtendedFraudAlert) {
            const extra = state.error
            state.error.extra = {
              isEFAFinished: false,
              ...extra,
            }
          }
          if (errorCode === ErrorCode.OrderAmountExceedsSpendCap) {
            state.error.extra = {
              availableToSpend: errorInfo.max_otb_amount,
            }
          }
        }
      })
      .addMatcher(confirmCheckout.matchFulfilled, (state, action) => {
        const insufficientFunds = action.payload.status === 'FAILURE'
        state.confirmed = action.payload
        state.status = insufficientFunds ? CheckoutStatus.Error : CheckoutStatus.Confirmed
        if (insufficientFunds) {
          state.error = {
            error: TerminalErrorCode.InsufficientFunds,
            errorCode: ErrorCode.PaymentDeclined,
          }
        }
      })
      .addMatcher<{
        meta: {
          rejectedWithValue: boolean
        }
        payload: {
          data?: PortalApiError
        }
      }>(identityVerify.matchRejected, (state, action) => {
        state.status = CheckoutStatus.Error
        state.error = { error: TerminalErrorCode.Default }

        if (action.meta.rejectedWithValue) {
          if (typeof action.payload?.data !== 'undefined' && action.payload?.data.errorCode !== null) {
            const { errorCode } = action.payload.data

            if (errorCode === ErrorCode.PaywatchCheckFailed) {
              state.error = {
                error: checkoutErrors[errorCode] ?? camelCase(errorCode),
                errorCode,
              }
            }
          }
        }
      })
      .addMatcher(updatePreferredPayday.matchFulfilled, (state, action) => {
        const { paymentSchedules: newPaymentSchedules, purchasePaymentAgreementDocumentId } = action.payload
        const { paymentSchedules, promotion } = enrichPaymentSchedules(
          state.started?.paymentSchedules ?? [],
          state.started?.promotion ?? null,
          newPaymentSchedules
        )

        const { started } = state
        const { enrichedCards, enrichedBankAccounts, enrichedPreferredBankAccount, enrichedPreferredCard } = getEnrichedCardsAndBankAccounts({
          enabledCardTypes: started?.enabledCardTypes ?? [],
          paymentSchedules: newPaymentSchedules,
          preferredBankAccount: started?.preferredBankAccount ?? null,
          preferredCard: started?.preferredCard ?? null,
          savedBankAccounts: started?.savedBankAccounts ?? [],
          savedCards: started?.savedCards ?? [],
        })

        if (typeof state.started !== 'undefined') {
          state.started = {
            ...state.started,
            paymentSchedules,
            promotion,
            purchasePaymentAgreementDocumentId,
          }
        }

        state.cards = enrichedCards
        state.preferredCard = enrichedPreferredCard
        state.bankAccounts = enrichedBankAccounts
        state.preferredBankAccount = enrichedPreferredBankAccount
      })
  },
})

export const {
  setAllowedQueryParam,
  addOAuthAuthorizationCodeIntoCallbackUrlQueryArgs,
  addOAuthAuthorizationErrorIntoCallbackUrlQueryArgs,
  resetCheckoutError,
  resetPreferredPaymentMethodErrors,
  resetToInitialCheckoutAmount,
  setCheckoutError,
  setCheckoutStatus,
  setCheckoutStatusToBeLoaded,
  setInitialData,
  setNotificationStatus,
  setReferrer,
  setDoItemsReconcile,
  setCardsAndBankAccounts,
  setCardForOverdueACH,
  setCardsForExpiryLimit,
  resetPreferredCardReason,
  setCashTooltipShown,
  setCreditCheckAgree,
  setPreferredCard,
  setSavedCards,
  setEnabledPBIAutoPay,
  setTermsDisclosureAgree,
  setEFAFinished,
  setLoggedInLoadingMessageKey,
  setCashAppPay,
  setSessionIdVisibility,
  resetSessionId,
  setConfirmTermsViewed,
  setIntegrationContext,
} = checkoutSlice.actions

export default checkoutSlice.reducer
